import { z } from "zod";
import { quoteOccurs } from "./quote-span.ts";
import type { Stage } from "./runner.ts";

export const PHONE_CALL_PATCH = "phone-call-goal-v1";
export const PHONE_CALL_OFF_PATCH = "phone-call-goal-off-v1";
export const PHONE_CALL_SUCCESS =
  "Agreement to an outbound phone call and a usable number for the intended contact.";
export const PHONE_CALL_ACTION = "collect_phone_call";
const text = z.string().trim().min(1).max(4000);
const reference = z.strictObject({ sourceId: text, quote: text });
export const PhoneSourceSchema = z.strictObject({
  id: text,
  messageId: text,
  authorId: text,
  threadAuthorId: text.optional(),
  side: z.enum(["lead_side", "seller_side", "third_party", "unknown"]),
  kind: z.enum(["body", "signature"]),
  text: z.string().max(100000),
  at: z.iso.datetime({ offset: true }),
});
export type PhoneSource = z.infer<typeof PhoneSourceSchema>;
export const PhoneProposalSchema = z.strictObject({
  contact: z
    .strictObject({ id: text, name: text, authorId: text.nullable(), evidence: reference })
    .nullable(),
  agreement: z.strictObject({
    state: z.enum([
      "absent",
      "agreed",
      "withdrawn",
      "email_only",
      "inbound_call",
      "video",
      "conditional",
    ]),
    basis: z.enum(["direct", "reported"]).nullable(),
    evidence: reference.nullable(),
  }),
  numbers: z
    .array(
      z.strictObject({
        raw: text,
        contactId: text,
        evidence: reference,
        source: z.enum(["body", "signature"]),
        preference: z.enum(["candidate", "nominated", "correction"]),
        kind: z.enum(["phone", "fax"]),
      }),
    )
    .max(20),
  restrictions: z
    .array(
      z.strictObject({
        kind: z.enum(["timing", "caller", "channel"]),
        text,
        evidence: reference,
      }),
    )
    .max(20),
  ambiguities: z.array(z.enum(["contact", "agreement", "number", "chronology", "caller"])).max(5),
});
export type PhoneProposal = z.infer<typeof PhoneProposalSchema>;
export const PhoneAssessmentSchema = z.strictObject({
  state: z.enum(["collecting", "ambiguous", "ready", "withdrawn", "failed"]),
  reason: z.enum([
    "missing_agreement",
    "missing_number",
    "missing_both",
    "conflicting_details",
    "invalid_evidence",
    "ready",
    "withdrawn",
    "different_channel",
  ]),
  contact: PhoneProposalSchema.shape.contact,
  agreement: PhoneProposalSchema.shape.agreement,
  number: z
    .strictObject({
      raw: text,
      comparison: text,
      international: z.string().nullable(),
      source: z.enum(["body", "signature", "rep"]),
      evidence: reference,
    })
    .nullable(),
  restrictions: PhoneProposalSchema.shape.restrictions,
  sources: z.array(PhoneSourceSchema),
});
export type PhoneAssessment = z.infer<typeof PhoneAssessmentSchema>;
export const PhoneRunRecordSchema = z.strictObject({
  version: z.literal(1),
  assessment: PhoneAssessmentSchema,
  basis: z
    .strictObject({
      outcomeRevision: z.number().int().nonnegative(),
      configurationRevision: z.number().int().nonnegative(),
      correctedAt: z.iso.datetime({ offset: true }).nullable(),
      correctionRevision: z.number().int().nonnegative().optional(),
    })
    .optional(),
});
export type PhoneRunRecord = z.infer<typeof PhoneRunRecordSchema>;

/** Syntactic plausibility only; never a claim that a line exists or is reachable. */
export function phoneValue(
  raw: string,
): { comparison: string; international: string | null } | null {
  const value = raw.trim();
  if (!/^\+?[\d\s().-]+(?:\s*(?:ext\.?|x)\s*\d{1,6})?$/iu.test(value)) return null;
  const parts = value.split(/\s*(?:ext\.?|x)\s*/iu);
  const digits = parts[0]?.replace(/\D/gu, "") ?? "";
  if (digits.length < 7 || digits.length > 15 || /^(\d)\1+$/u.test(digits)) return null;
  const extension = parts[1];
  const base = `${value.startsWith("+") ? "+" : ""}${digits}`;
  return {
    comparison: `${base}${extension ? `x${extension}` : ""}`,
    international: value.startsWith("+") ? base : null,
  };
}

/** Reject a known non-agreement, not recognize consent from keywords. Passing this guard grants
 * nothing: the classifier's semantic verdict, exact source and identity checks still apply. */
function numberOrGenericInterest(quote: string): boolean {
  if (phoneValue(quote)) return true;
  const generic = /\b(?:interested|details|information|number|mobile)\b/iu.test(quote);
  const acceptance =
    /\b(?:yes|sure|okay|ok|agree|agreed|accept|accepted|consent|expect|expects|works|fine|happy|glad|go ahead|please do)\b|\b(?:call|ring|phone)\s+(?:me|us|him|her|them)\b|\b(?:can|may|could)\s+(?:call|ring|phone)\b|\b(?:speak|talk)\s+(?:with|to)\b|\b(?:take|have|do|want|like|welcome)\s+(?:an?\s+)?(?:phone\s+)?call\b|(?:^|[:.!?]\s*)(?:please\s+)?(?:call|phone|ring)\b/iu.test(
      quote,
    );
  return generic && !acceptance;
}

function numberPreferenceRank(
  candidate: PhoneProposal["numbers"][number],
  source: PhoneSource,
): number {
  if (source.kind === "signature" || candidate.preference === "candidate") return 0;
  const index = source.text.indexOf(candidate.raw);
  if (index < 0) return 0;
  const prefix =
    source.text
      .slice(0, index)
      .split(/[.!?;\n]/u)
      .at(-1) ?? "";
  const suffix = source.text.slice(index + candidate.raw.length).split(/[.!?;\n]/u)[0] ?? "";
  const clause = `${prefix} ${suffix}`;
  if (/\b(?:do not|don't|never|avoid)\s+(?:use|call|ring|dial)\b/iu.test(clause)) return 0;
  // A body label is not a preference. Require an authored instruction tied to this number,
  // rather than allowing the classifier's ranking to silently defeat a conflicting signature.
  const nominated =
    /\b(?:use|call|ring|dial|reach)\b[^\d]{0,80}$|\b(?:best|preferred|correct|corrected|new|updated)\s+(?:phone\s+)?number\b[^\d]{0,40}$/iu.test(
      prefix,
    );
  if (!nominated) return 0;
  return candidate.preference === "correction" &&
    /\b(?:instead|correct|corrected|new|updated|replace)\b/iu.test(clause)
    ? 2
    : 1;
}

type Evidence = z.infer<typeof reference>;
type SourceFor = (evidence: Evidence) => PhoneSource | null;
type ValidNumber = { number: NonNullable<PhoneAssessment["number"]>; rank: number; at: string };

function renumberContact(
  numbers: PhoneProposal["numbers"],
  from: string,
  to: string,
): PhoneProposal["numbers"] {
  return numbers.map((number) => ({
    ...number,
    contactId: number.contactId === from ? to : number.contactId,
  }));
}

/**
 * Resolve the current thread handle through the host-owned stable identity mapping. Both handles
 * describe the same writer; a different or ambiguous mapping remains a hard failure later.
 */
function withCanonicalAuthor(
  proposal: PhoneProposal,
  sources: readonly PhoneSource[],
): PhoneProposal {
  const supplied = proposal.contact?.authorId;
  if (!proposal.contact || !supplied) return proposal;
  const ids = [
    ...new Set(
      sources
        .filter((source) => source.authorId === supplied || source.threadAuthorId === supplied)
        .map((source) => source.authorId),
    ),
  ];
  const canonical = ids.length === 1 ? ids[0] : undefined;
  if (!canonical) return proposal;
  const oldId = proposal.contact.id;
  const id = oldId === supplied ? canonical : oldId;
  return {
    ...proposal,
    contact: { ...proposal.contact, authorId: canonical, id },
    numbers: renumberContact(proposal.numbers, oldId, id),
  };
}

/** Ordinary message handles are equivalent only when the exact quote identifies one owned span. */
function withCanonicalEvidence(
  proposal: PhoneProposal,
  sources: readonly PhoneSource[],
): PhoneProposal {
  const canonical = (evidence: Evidence): Evidence => {
    if (sources.some((source) => source.id === evidence.sourceId)) return evidence;
    const matches = sources.filter(
      (source) =>
        source.messageId === evidence.sourceId && quoteOccurs(source.text, evidence.quote),
    );
    return matches.length === 1 && matches[0] ? { ...evidence, sourceId: matches[0].id } : evidence;
  };
  return {
    ...proposal,
    contact: proposal.contact
      ? { ...proposal.contact, evidence: canonical(proposal.contact.evidence) }
      : null,
    agreement: {
      ...proposal.agreement,
      evidence: proposal.agreement.evidence ? canonical(proposal.agreement.evidence) : null,
    },
    numbers: proposal.numbers.map((number) => ({
      ...number,
      evidence: canonical(number.evidence),
    })),
    restrictions: proposal.restrictions.map((value) => ({
      ...value,
      evidence: canonical(value.evidence),
    })),
  };
}

/** The lead-side or third-party source that exactly contains the quote, or null. */
function evidenceSources(sources: readonly PhoneSource[]): SourceFor {
  const byId = new Map(sources.map((source) => [source.id, source]));
  return (evidence) => {
    const source = byId.get(evidence.sourceId);
    if (!source || (source.side !== "lead_side" && source.side !== "third_party")) return null;
    return quoteOccurs(source.text, evidence.quote) ? source : null;
  };
}

/** A reported agreement names a nonwriter, so the contact is keyed by the referrer and the name. */
function withReferralContact(proposal: PhoneProposal, agreementSource: PhoneSource): PhoneProposal {
  if (proposal.agreement.basis !== "reported" || !proposal.contact) return proposal;
  const priorId = proposal.contact.id;
  const id = `referral:${agreementSource.authorId}:${proposal.contact.name.normalize("NFC").trim().toLowerCase().replace(/\s+/gu, " ")}`;
  return {
    ...proposal,
    contact: { ...proposal.contact, id },
    numbers: renumberContact(proposal.numbers, priorId, id),
  };
}

function evidenceUnsupported(
  proposal: PhoneProposal,
  sourceFor: SourceFor,
  agreementSource: PhoneSource | null,
  contactSource: PhoneSource | null,
): boolean {
  if (proposal.contact && !contactSource) return true;
  if (proposal.agreement.evidence && !agreementSource) return true;
  return proposal.restrictions.some((restriction) => !sourceFor(restriction.evidence));
}

/** An agreement whose author, basis or contact identity does not hold together. */
function agreementUnattributed(
  proposal: PhoneProposal,
  agreementSource: PhoneSource | null,
  contactSource: PhoneSource | null,
): boolean {
  const { contact, agreement } = proposal;
  if (!contact || !agreementSource || agreementSource.kind !== "body" || !agreement.basis)
    return true;
  if (agreement.basis === "direct")
    return contact.authorId !== agreementSource.authorId || contact.id !== contact.authorId;
  return (
    contactSource?.authorId !== agreementSource.authorId || !contact.id.startsWith("referral:")
  );
}

/** The same author later asked not to be called. */
function refusedAfterAgreement(sources: readonly PhoneSource[], agreement: PhoneSource): boolean {
  return sources.some(
    (source) =>
      source.kind === "body" &&
      source.authorId === agreement.authorId &&
      Date.parse(source.at) > Date.parse(agreement.at) &&
      /\b(?:do not call|don.t call|stop calling|email only)\b/iu.test(source.text),
  );
}

function faxLine(source: PhoneSource, raw: string): boolean {
  return source.text.split("\n").some((line) => line.includes(raw) && /\bfax\b/iu.test(line));
}

/** The usable numbers for the contact; null when a number's evidence does not hold. */
function validNumbers(proposal: PhoneProposal, sourceFor: SourceFor): ValidNumber[] | null {
  const valid: ValidNumber[] = [];
  for (const candidate of proposal.numbers) {
    if (candidate.kind === "fax" || /\bfax\b/iu.test(candidate.evidence.quote)) continue;
    const source = sourceFor(candidate.evidence);
    if (!source || !quoteOccurs(candidate.evidence.quote, candidate.raw)) return null;
    const parsed = phoneValue(candidate.raw);
    if (!parsed || candidate.contactId !== proposal.contact?.id) continue;
    if (faxLine(source, candidate.raw)) continue;
    if (source.kind === "signature" && source.authorId !== proposal.contact.authorId) continue;
    valid.push({
      number: { raw: candidate.raw, ...parsed, source: source.kind, evidence: candidate.evidence },
      rank: numberPreferenceRank(candidate, source),
      at: source.at,
    });
  }
  return valid;
}

/** The preferred number, or "competing" when another number is as preferred. */
function chosenNumber(valid: ValidNumber[]): PhoneAssessment["number"] | "competing" {
  valid.sort(
    (a, b) =>
      Number(b.rank > 0) - Number(a.rank > 0) ||
      (b.rank > 0 ? Date.parse(b.at) - Date.parse(a.at) : 0) ||
      b.rank - a.rank,
  );
  const chosen = valid[0];
  if (!chosen) return null;
  const competing = valid.some(
    (value) =>
      value.number.comparison !== chosen.number.comparison &&
      (chosen.rank === 0 || (value.rank === chosen.rank && value.at === chosen.at)),
  );
  return competing ? "competing" : chosen.number;
}

function assessment(
  proposal: PhoneProposal,
  sources: readonly PhoneSource[],
  state: PhoneAssessment["state"],
  reason: PhoneAssessment["reason"],
  number: PhoneAssessment["number"] = null,
): PhoneAssessment {
  return {
    state,
    reason,
    contact: proposal.contact,
    agreement: proposal.agreement,
    number,
    restrictions: proposal.restrictions,
    sources: [...sources],
  };
}

type Verdict = readonly [PhoneAssessment["state"], PhoneAssessment["reason"]];

/** The checks that end an assessment before any number is read, in their order. */
function earlyVerdict(
  proposal: PhoneProposal,
  sources: readonly PhoneSource[],
  terminal: boolean,
  evidence: {
    sourceFor: SourceFor;
    agreement: PhoneSource | null;
    contact: PhoneSource | null;
  },
): Verdict | null {
  const { agreement } = proposal;
  if (evidenceUnsupported(proposal, evidence.sourceFor, evidence.agreement, evidence.contact))
    return ["failed", "invalid_evidence"];
  if (terminal || (agreement.state === "withdrawn" && evidence.agreement?.kind === "body"))
    return ["withdrawn", "withdrawn"];
  if (proposal.ambiguities.length) return ["ambiguous", "conflicting_details"];
  if (agreement.state === "withdrawn") return ["failed", "invalid_evidence"];
  if (
    agreement.state === "agreed" &&
    agreementUnattributed(proposal, evidence.agreement, evidence.contact)
  )
    return ["failed", "invalid_evidence"];
  if (evidence.agreement && refusedAfterAgreement(sources, evidence.agreement))
    return ["ambiguous", "conflicting_details"];
  return null;
}

function finalVerdict(
  agreementState: PhoneProposal["agreement"]["state"],
  agreed: boolean,
  number: PhoneAssessment["number"],
): Verdict {
  if (["email_only", "inbound_call", "video"].includes(agreementState))
    return ["collecting", "different_channel"];
  if (agreed && number) return ["ready", "ready"];
  if (agreed) return ["collecting", "missing_number"];
  return ["collecting", number ? "missing_agreement" : "missing_both"];
}

/** Only validated supplied evidence can become contact readiness. No IO, clock or provider writes. */
export function assessPhoneCall(
  supplied: PhoneProposal,
  sources: readonly PhoneSource[],
  terminal = false,
): PhoneAssessment {
  let proposal = withCanonicalEvidence(withCanonicalAuthor(supplied, sources), sources);
  const sourceFor = evidenceSources(sources);
  const agreementSource = proposal.agreement.evidence
    ? sourceFor(proposal.agreement.evidence)
    : null;
  const contactSource = proposal.contact ? sourceFor(proposal.contact.evidence) : null;
  if (agreementSource) proposal = withReferralContact(proposal, agreementSource);
  const early = earlyVerdict(proposal, sources, terminal, {
    sourceFor,
    agreement: agreementSource,
    contact: contactSource,
  });
  if (early) return assessment(proposal, sources, ...early);
  let agreed = proposal.agreement.state === "agreed";
  const quote = proposal.agreement.evidence?.quote;
  if (agreed && quote !== undefined && numberOrGenericInterest(quote)) {
    // A real quoted number or expression of interest is still not a quoted call agreement.
    // Preserve the independently validated contact/number for the collection draft.
    proposal = { ...proposal, agreement: { state: "absent", basis: null, evidence: null } };
    agreed = false;
  }
  const valid = validNumbers(proposal, sourceFor);
  if (!valid) return assessment(proposal, sources, "failed", "invalid_evidence");
  const number = chosenNumber(valid);
  if (number === "competing")
    return assessment(proposal, sources, "ambiguous", "conflicting_details");
  return assessment(
    proposal,
    sources,
    ...finalVerdict(proposal.agreement.state, agreed, number),
    number,
  );
}

const PHONE_CALL_INSTRUCTIONS = `For phone_call, return phone_call evidence separately from classification. Read the full current thread, not earlier campaigns. Agreement must be to an outbound phone call about the configured offer; a yes to a brochure is not agreement. A clearly attributed body or signature phone counts. National-format numbers are usable as supplied; do not infer a country. Exclude fax numbers, seller/quoted/internal numbers and automatic replies. A named referral without rejection is an active routing request in ordinary classification (route_to_person), even if it says the person might be right. Uncertainty about the contact is not a soft decline. It lacks call agreement until explicitly reported or given directly. A referral counts only if a lead-side person explicitly reports that the named recipient expects or agrees to the call: basis reported, keeping referrer evidence. Direct agreement uses the recipient's stable authorId from phone_sources; threadAuthorId links it to the ordinary thread author. Quote exact supplied phone sources; contact.id is its authorId for a writer, or referral:<sourceId> for a named nonwriter. Explicit corrected numbers supersede stale signatures; nominate nothing when competing numbers are unresolved. Accepted future calls retain timing restrictions and are not timing refusals. Preserve named caller and channel restrictions. Email-only, incoming-call and video preferences are not outbound agreement. In ordinary classification, a channel preference that keeps the conversation open is an active objection or request, not a final refusal or opt-out; only an explicit rejection of further contact or the offer closes the conversation. A withdrawal revokes previous readiness. Never infer agreement from a number or from seller promises. Generic interest plus a number still has agreement state absent: for example, "Interested in the details. My number is ..." contains no acceptance of the call. Quote the actual acceptance clause, not a phone number or generic interest as a substitute. List every plausible number with its owner and preference. Signature numbers always have preference candidate; only an explicit authored-body preference can nominate or correct a number. Missing information is collecting, not an ambiguity: leave ambiguities empty when a number or agreement is simply absent. Use ambiguities only for unresolved competing interpretations or conflicting details. Missing or conflicting evidence is not success.`;

export const PhoneReconcileRequestSchema = z.strictObject({
  workspaceId: z.number().int().positive(),
  campaignId: z.number().int().positive(),
  conversationId: z.string().min(1),
  assessment: PhoneAssessmentSchema,
});
export type PhoneReconcileRequest = z.infer<typeof PhoneReconcileRequestSchema>;
/** Apply evidenced rep amendments until a later explicit authored correction supersedes them. */
export function reconcilePhoneCorrection(
  assessment: PhoneAssessment,
  previous: {
    assessment: PhoneAssessment;
    correction: "number" | "withdrawal" | "details" | null;
  } | null,
): PhoneAssessment {
  if (!previous || assessment.state === "failed" || assessment.state === "withdrawn")
    return assessment;
  const newest = Math.max(0, ...previous.assessment.sources.map((source) => Date.parse(source.at)));
  const newerBody = (id: string | undefined) =>
    assessment.sources.some(
      (source) => source.id === id && source.kind === "body" && Date.parse(source.at) > newest,
    );
  const newAgreement = newerBody(assessment.agreement.evidence?.sourceId);
  const newNumber = newerBody(assessment.number?.evidence.sourceId);
  if (
    previous.assessment.state === "withdrawn" &&
    !(newAgreement && assessment.agreement.state === "agreed")
  )
    return keptWithdrawal(assessment, previous.assessment);
  if (!previous.correction) return assessment;

  const newRestriction = assessment.restrictions.some((value) =>
    newerBody(value.evidence.sourceId),
  );
  if (previous.correction === "withdrawal" && !newAgreement)
    return keptWithdrawal(assessment, previous.assessment);
  if (previous.correction === "details" && !newAgreement && !newNumber && !newRestriction)
    return { ...previous.assessment, sources: assessment.sources };
  if (
    previous.correction === "number" &&
    !newNumber &&
    previous.assessment.contact?.id === assessment.contact?.id
  )
    return withCorrectedNumber(assessment, previous.assessment.number);
  return assessment;
}

function keptWithdrawal(assessment: PhoneAssessment, previous: PhoneAssessment): PhoneAssessment {
  return {
    ...assessment,
    state: "withdrawn",
    reason: "withdrawn",
    agreement: previous.agreement,
    number: null,
  };
}

/** A rep's number correction stands until the lead supplies a newer number. */
function withCorrectedNumber(
  assessment: PhoneAssessment,
  number: PhoneAssessment["number"],
): PhoneAssessment {
  const ready = assessment.agreement.state === "agreed" && number !== null;
  return {
    ...assessment,
    number,
    state: ready ? "ready" : assessment.state,
    reason: ready ? "ready" : assessment.reason,
  };
}

const PHONE_PLANNING_INSTRUCTIONS =
  "\n## Phone-call collection\n\nWhen the configured goal is `phone_call`, use the host's `phone_assessment` as the current contact, agreement and number verdict. `collect_phone_call` asks the lead only for missing agreement or contact details; it needs no meeting time, duration, calendar or timezone. Neither a missing phone number nor missing call agreement is a seller-context question. Never put either in `required_questions`. An ordinary classifier ask such as `propose_times` does not override this goal or create an appointment requirement.\n\nGeneric interest in details is an information request, not call agreement. If the requested details are unspecified and no specific approved answer can be identified, select request_clarification and ask which details are wanted before collecting call agreement. If a supported specific answer is available, answer it first. Build the response plan for the selected inbound message. Older agreements and supplied numbers remain history and commitments, not fresh speech acts to answer again. Read intervening seller replies before deciding an older question is still owed. Do not repeat a question already answered, put it in `speech_acts`, or select `answer` merely because that question occurred earlier. A supplied number, a correction or a thanks after agreement normally selects `acknowledge`; retain any genuinely unanswered current question. Do not invent action labels for acknowledgements: the reply itself acknowledges, so `actions` may be empty.\n\nOnce ready, acknowledge the agreed call and receipt of the details without promising its execution. A correction calls for acknowledging the corrected number; a channel restriction calls for acknowledging that preference. These acknowledgements are complete reply work. Do not create a new invitation, collection question, seller promise or appointment just to make progress. A later thanks does not reopen collection or require repeating the whole agreement.\n";
const PHONE_REPLY_INSTRUCTIONS =
  "\n## Phone-call goal acknowledgement\n\nFor configured `phone_call`, the host's `phone_assessment` owns readiness. Agreement plus a usable number leads to a specific acknowledgement, with any still-owed answer, not another collection question or booking CTA. Receiving or correcting contact details and accepting a communication restriction are meaningful conversational moves: confirming that you understood them is enough. Do not demand an extra promise, deadline, question, pitch or new information to make this acknowledgement useful. This is a scoped exception to treating acknowledgements as having no move. A later thanks can receive a short courtesy reply without repeating the entire agreement.\n\nDistinguish acknowledging a fact from promising an action. Wording such as \u201cThanks, I have your number for the call\u201d, \u201cThanks for confirming the corrected number\u201d, or \u201cUnderstood, email only\u201d acknowledges the supplied details or preference. These are examples of the distinction, not mandatory templates. \u201cWe will call tomorrow\u201d, \u201cI've handed this over\u201d, or \u201cThe call is booked\u201d asserts separate actions and needs independent authority or execution evidence. A number or restriction from the lead supplies no such authority. Do not rewrite a specific acknowledgement into \u201cwe'll use it\u201d or another promise merely to improve its prose. Preserve supported answers and restrictions.\n\nCheck the complete thread for prior answers. Earlier questions already answered by a seller message are not owed again. Earlier accepted call agreement is context for the current acknowledgement, not a demand to book or repeat consent. Do not infer new current speech acts from historical messages just to require a larger reply.\n";
const PHONE_AUDIT_INSTRUCTIONS =
  "\nAudit every speech act actually supplied in `analysis.fact_plan.speech_acts` using its exact ID; do not silently drop entries. For an independently discovered omitted act, use only the selected inbound message as its source. Never create an omitted act from an earlier message. Keep audit source quotes exact and validate response quotes against the candidate before returning. An acknowledgement of received details can be `supported` without a future-action promise.\n";

/** Added only to host-assessed phone turns; the frozen general-goal prompts remain unchanged. */
function phoneStageInstructions(stage: Stage): string {
  if (stage === "planning") return PHONE_PLANNING_INSTRUCTIONS;
  if (stage === "grounding") return PHONE_REPLY_INSTRUCTIONS + PHONE_AUDIT_INSTRUCTIONS;
  if (stage === "draft" || stage === "prose") return PHONE_REPLY_INSTRUCTIONS;
  return "";
}

/** Phone evidence is asked of the classification call only; true when this call asks for it. */
export function asksPhoneEvidence(
  stage: Stage,
  runtime: { readonly phone_call?: number | undefined },
): boolean {
  const phoneCall = runtime.phone_call === 1;
  if (phoneCall && stage !== "classification")
    throw new Error("Phone evidence belongs to classification only");
  return phoneCall;
}

/** Whether the stage payload carries the host's phone assessment. */
export function phoneAssessed(payload: object): boolean {
  return "phone_assessment" in payload && Boolean(payload.phone_assessment);
}

/** A stage prompt with the phone evidence instructions and the assessed turn's guidance, as both transports build it. */
export function phonePrompt(
  stage: Stage,
  base: string,
  phoneCall: boolean,
  assessed: boolean,
): string {
  const prompt = phoneCall ? `${base}\n${PHONE_CALL_INSTRUCTIONS}` : base;
  return prompt + (assessed ? phoneStageInstructions(stage) : "");
}
